import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useEffect, useState } from 'react';
import NftLeftDetails from '@/Component/NftLeftDetails';
import NftLeftDetailsSkeleton from '@/Component/NftLeftDetailsSkeleton';
import NftRightDetails from '@/Component/NftRightDetails';
import NftRightDetailsSkeleton from '@/Component/NftRightDetailsSkeleton';
import RenderIf from '@/Component/RenderIf';
import { useCurrencyRate } from '@/lib/hooks/useCurrencyRate';
import { NftSoldDetail } from '@/lib/providers/NftContextProvider';

type Props = {
  loading: boolean;
  selectedNft?: NftSoldDetail;
  isSaleStarted: boolean;
  isSaleEnded: boolean;
  endDate?: string;
  balanceTokenQuantity?: number;
  isContactGallery: boolean;
  buyersList?: string[];
};

const NftDetailsLayout: React.FC<Props> = ({
  selectedNft,
  loading,
  isSaleStarted,
  isSaleEnded,
  endDate,
  balanceTokenQuantity,
  isContactGallery,
  buyersList = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { priceData } = useCurrencyRate();

  const [visible, setVisible] = useState<boolean>(false);
  const [index, setIndex] = useState<number>(0);

  const handlePreviewImage = useCallback(() => {
    setVisible((prev) => {
      if (prev) {
        window.history.back();
      }

      return !prev;
    });
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (visible) {
        setVisible(false);
      }
    };

    if (visible) {
      window.history.pushState(null, '', window.location.href);
    }
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [visible]);

  return (
    <div style={{ flex: 1 }}>
      <Box
        sx={{
          fontWeight: 500,
          fontSize: '16px',
          padding: {
            lg: '0px 120px 120px 120px',
            md: '0px 120px 120px 120px',
            xs: '16px 0 0 0'
          },
          marginTop: isMobile ? '12px' : '80px',
          textAlign: isMobile ? 'center' : 'left',
          backgroundColor: theme?.palette?.background?.paper
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} md={6}>
              <RenderIf condition={isMobile && loading}>
                <NftRightDetailsSkeleton isMobile={isMobile} />
              </RenderIf>
              <RenderIf condition={isMobile && !loading}>
                <NftRightDetails
                  index={index}
                  visible={visible}
                  isMobile={isMobile}
                  isSaleStarted={isSaleStarted}
                  isSaleEnded={isSaleEnded}
                  endDate={endDate}
                  selectedNft={selectedNft}
                  priceData={priceData}
                  setIndex={setIndex}
                  handlePreviewImage={handlePreviewImage}
                  balanceTokenQuantity={balanceTokenQuantity}
                  isContactGallery={isContactGallery}
                  buyersList={buyersList}
                />
              </RenderIf>
              <RenderIf condition={loading}>
                <NftLeftDetailsSkeleton isMobile={isMobile} />
                <RenderIf.Else>
                  <NftLeftDetails
                    isMobile={isMobile}
                    selectedNft={selectedNft}
                    index={index}
                    visible={visible}
                    setIndex={setIndex}
                    handlePreviewImage={handlePreviewImage}
                  />
                </RenderIf.Else>
              </RenderIf>
            </Grid>
            <RenderIf condition={!isMobile}>
              <Grid item xs={12} md={6}>
                <RenderIf condition={loading}>
                  <NftRightDetailsSkeleton isMobile={isMobile} />
                  <RenderIf.Else>
                    <NftRightDetails
                      index={index}
                      visible={visible}
                      isMobile={isMobile}
                      isSaleStarted={isSaleStarted}
                      isSaleEnded={isSaleEnded}
                      endDate={endDate}
                      selectedNft={selectedNft}
                      priceData={priceData}
                      setIndex={setIndex}
                      handlePreviewImage={handlePreviewImage}
                      balanceTokenQuantity={balanceTokenQuantity}
                      isContactGallery={isContactGallery}
                      buyersList={buyersList}
                    />
                  </RenderIf.Else>
                </RenderIf>
              </Grid>
            </RenderIf>
          </Grid>
        </Container>
      </Box>
    </div>
  );
};

export default NftDetailsLayout;
